import React, { useEffect, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, ChevronRight } from "lucide-react";
import DocsNavigation from "../components/landing/DocsNavigation";
import { Footer } from "../components/landing/Footer";
import {
  CATEGORIES,
  getArticle,
  getArticlesByCategory,
} from "../data/helpArticles";
import { ArticleRenderer } from "../components/help/ArticleRenderer";

const HelpArticlePage = () => {
  const { slug } = useParams();
  const article = useMemo(() => getArticle(slug), [slug]);

  useEffect(() => {
    const html = document.documentElement;
    const wasLight = html.classList.contains("light");
    if (wasLight) {
      html.classList.remove("light");
      html.classList.add("dark");
    }
    window.scrollTo(0, 0);
    return () => {
      if (wasLight) {
        html.classList.remove("dark");
        html.classList.add("light");
      }
    };
  }, [slug]);

  if (!article) {
    return <Navigate to="/help" replace />;
  }

  const category = CATEGORIES.find((c) => c.id === article.category);
  const siblings = getArticlesByCategory(article.category);
  const currentIdx = siblings.findIndex((a) => a.slug === slug);
  const prev = currentIdx > 0 ? siblings[currentIdx - 1] : null;
  const next =
    currentIdx >= 0 && currentIdx < siblings.length - 1
      ? siblings[currentIdx + 1]
      : null;

  // Sidebar nav: all articles in this category
  return (
    <div
      className="min-h-screen bg-space-900 relative"
      data-testid="help-article-page"
    >
      <DocsNavigation />

      <main className="relative pt-28 pb-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-28 self-start">
            <Link
              to="/help"
              className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal-400 transition-colors mb-6"
              data-testid="help-article-back-link"
            >
              <ArrowLeft size={16} />
              All articles
            </Link>
            {category && (
              <div className="mb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">
                {category.title}
              </div>
            )}
            <nav className="space-y-1">
              {siblings.map((a) => (
                <Link
                  key={a.slug}
                  to={`/help/${a.slug}`}
                  className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                    a.slug === slug
                      ? "bg-teal-400/10 text-teal-300 border border-teal-400/20"
                      : "text-gray-400 hover:text-white hover:bg-white/[0.04] border border-transparent"
                  }`}
                  data-testid={`help-sidebar-link-${a.slug}`}
                >
                  {a.title}
                </Link>
              ))}
            </nav>
          </aside>

          {/* Article body */}
          <article className="min-w-0">
            <header className="mb-10 pb-6 border-b border-white/[0.06]">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                <Link to="/help" className="hover:text-teal-300">
                  Help Center
                </Link>
                <ChevronRight size={14} />
                {category && (
                  <>
                    <Link
                      to={`/help/category/${category.id}`}
                      className="hover:text-teal-300"
                    >
                      {category.title}
                    </Link>
                    <ChevronRight size={14} />
                  </>
                )}
                <span className="text-gray-400 truncate">{article.title}</span>
              </div>
              <h1 className="font-display font-semibold text-3xl sm:text-4xl text-white mb-3 leading-tight">
                {article.title}
              </h1>
              <p className="text-gray-400 text-base sm:text-lg max-w-2xl">
                {article.description}
              </p>
              <div className="flex items-center gap-4 text-xs text-gray-500 mt-4">
                <span className="flex items-center gap-1">
                  <Clock size={11} />
                  {article.readTime} min read
                </span>
                <span>Updated {article.updated}</span>
              </div>
            </header>

            <ArticleRenderer blocks={article.blocks} />

            {/* Prev / Next */}
            <div className="mt-14 pt-8 border-t border-white/[0.06] grid grid-cols-1 md:grid-cols-2 gap-3">
              {prev ? (
                <Link
                  to={`/help/${prev.slug}`}
                  className="group p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-teal-400/30 hover:bg-white/[0.05] transition-all"
                  data-testid="help-article-prev"
                >
                  <div className="text-xs text-gray-500 mb-1">← Previous</div>
                  <div className="text-white font-medium group-hover:text-teal-300 transition-colors">
                    {prev.title}
                  </div>
                </Link>
              ) : (
                <div />
              )}
              {next ? (
                <Link
                  to={`/help/${next.slug}`}
                  className="group p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-teal-400/30 hover:bg-white/[0.05] transition-all md:text-right"
                  data-testid="help-article-next"
                >
                  <div className="text-xs text-gray-500 mb-1">Next →</div>
                  <div className="text-white font-medium group-hover:text-teal-300 transition-colors">
                    {next.title}
                  </div>
                </Link>
              ) : (
                <div />
              )}
            </div>

            {/* Still need help */}
            <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-teal-400/[0.06] to-teal-400/[0.02] border border-teal-400/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <div className="text-white font-semibold">
                  Still have questions?
                </div>
                <div className="text-sm text-gray-400">
                  Reach our team — replies within 1 business day.
                </div>
              </div>
              <Link
                to="/support"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-teal-400 hover:bg-teal-500 text-space-900 font-medium text-sm transition-colors"
                data-testid="help-article-contact-cta"
              >
                Contact Support
                <ArrowRight size={14} />
              </Link>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HelpArticlePage;
